import { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Instagram } from "lucide-react";
import useSWR from "swr";
import { API, fetcher } from "../lib/api";

const INSTAGRAM_URL = "https://www.instagram.com/sparkwear___";
const PREFERRED = [
  "Cloud Panda Onesie",
  "Flower Girl 3-Piece Set",
  "Supercute 2-in-1",
  "Bunny Backpack",
  "Green & Red High Bow",
  "Custom Cartoon Tank Top",
];

export default function InstagramSection() {
  const { data: products = [] } = useSWR(`${API}/products`, fetcher);

  const pics = useMemo(() => {
    const withPics = products.filter((p) => p.image);
    const preferred = PREFERRED.map((name) =>
      withPics.find((p) => p.name?.startsWith(name))
    ).filter(Boolean);
    const rest = withPics.filter((p) => !preferred.includes(p));
    return [...preferred, ...rest].slice(0, 6);
  }, [products]);

  if (!pics.length) return null;

  return (
    <section className="py-14 md:py-20" data-testid="instagram-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="text-center">
          <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-blush-700">
            @sparkwear___ on Instagram
          </p>
          <h2 className="mt-3 font-display text-3xl font-semibold text-ink sm:text-4xl md:text-5xl">
            Follow the sparkle
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm text-muted sm:text-base">
            New arrivals, behind-the-scenes happy moments and little wins — join our
            Sparkwear family on Instagram.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-3 gap-2 sm:gap-3 md:grid-cols-6">
          {pics.map((p, i) => (
            <motion.a
              key={p.id}
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`See ${p.name} on our Instagram`}
              data-testid={`instagram-tile-${i + 1}`}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.45, delay: i * 0.06 }}
              className="group relative block overflow-hidden rounded-2xl bg-blush-50"
            >
              <img
                src={p.image}
                alt={`${p.name} — Sparkwear kidswear on Instagram`}
                loading="lazy"
                className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <span className="absolute inset-0 flex items-center justify-center bg-ink/0 opacity-0 transition-all duration-300 group-hover:bg-ink/45 group-hover:opacity-100">
                <Instagram className="h-6 w-6 text-white" />
              </span>
            </motion.a>
          ))}
        </div>

        <div className="mt-8 text-center">
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-follow-button"
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blush-600 via-pink-500 to-amber-500 px-8 py-4 font-display text-sm font-semibold text-white shadow-lg transition-transform duration-300 hover:scale-[1.04]"
          >
            <Instagram className="h-5 w-5" />
            Follow @sparkwear___
          </a>
        </div>
      </div>
    </section>
  );
}
